import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Wand2 } from "lucide-react";
import Axios, { authHeaders } from "../../utils/axios";
import { useToast } from "../../context/TostContext";

/**
 * "Vectorise" a georeferenced PDF sheet in one step: R-Point reads it, applies
 * the authority's profile (or its own guesses) and builds a draft, then the
 * admin checks the preview and publishes. Settings stay on the import page
 * for the sheets the guesses get wrong.
 *
 * row: a map row ({ id, title }); null hides the modal.
 */
export default function VectoriseModal({ row, onHide }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [defaults, setDefaults] = useState(null);
  const [authority, setAuthority] = useState("");
  const [planName, setPlanName] = useState("");
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    setDefaults(null);
    if (!row) return;
    Axios.get("/maps/gis-imports/defaults", { params: { map_id: row.id }, ...authHeaders() })
      .then((res) => {
        const d = res.data?.data || {};
        setDefaults(d);
        setAuthority(d.authority || "");
        setPlanName(d.plan_name || row.title || "");
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Could not read the sheet");
        onHide();
      });
  }, [row, toast, onHide]);

  const open = defaults?.last_import && !["published", "failed"].includes(defaults.last_import.status);

  const start = async () => {
    setStarting(true);
    try {
      const res = await Axios.post(
        "/maps/gis-imports",
        { map_id: row.id, authority: authority.trim(), plan_name: planName.trim(), auto: true },
        authHeaders()
      );
      navigate(`/gis-imports/${res.data?.data?.id}`);
    } catch (err) {
      const existing = err.response?.data?.data?.import_id;
      if (err.response?.status === 409 && existing) {
        navigate(`/gis-imports/${existing}`);
        return;
      }
      toast.error(err.response?.data?.message || "Could not start");
      setStarting(false);
    }
  };

  return (
    <Modal show={!!row} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">Vectorise {row?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="small">
        {!defaults ? (
          <div className="text-center py-3"><Spinner size="sm" /></div>
        ) : open ? (
          <Alert variant="info" className="mb-0">
            This sheet already has a draft in progress. Open it to check and publish, or discard it there to start again.
          </Alert>
        ) : (
          <>
            <p className="text-muted">
              Zoning, plots and their numbers are read from the PDF and built as a draft. Nothing goes on the
              map until you check the preview and publish.
            </p>
            <div className="row g-3">
              <div className="col-4">
                <Form.Label className="small fw-medium">Authority</Form.Label>
                <Form.Control
                  value={authority}
                  maxLength={30}
                  onChange={(e) => setAuthority(e.target.value.toUpperCase())}
                  placeholder="e.g. AUDA"
                />
              </div>
              <div className="col-8">
                <Form.Label className="small fw-medium">Plan name</Form.Label>
                <Form.Control value={planName} onChange={(e) => setPlanName(e.target.value)} placeholder="e.g. Ahmedabad DP 2021" />
              </div>
            </div>
            <div className="text-muted mt-2">
              {defaults.profile && defaults.authority === authority
                ? `Uses the "${defaults.profile.name}" profile (v${defaults.profile.version}).`
                : authority
                  ? `Layers are matched from their names; zone codes come from ${authority}'s saved colours.`
                  : "Sheets of one plan share an authority; zone codes are remembered per authority."}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>Cancel</Button>
        {open ? (
          <Button onClick={() => navigate(`/gis-imports/${defaults.last_import.id}`)}>Open the draft</Button>
        ) : (
          <Button
            className="d-flex align-items-center gap-2"
            disabled={!defaults || starting || !authority.trim() || !planName.trim()}
            onClick={start}
          >
            {starting ? <Spinner size="sm" /> : <Wand2 size={16} />} Vectorise
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
